'use client'

import { ManualDefaultVariantSelector } from '@/components/products/default-variant/ManualDefaultVariantSelector'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Medium, Muted, XSmall } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import {
    AdminLandingPageLink,
    primaryProductVariantsAndOptions,
} from '@storefronts/common-types'
import React, { useCallback, useMemo, useState } from 'react'
import { Icon } from '@/components/Icon'
import {
    type ProductPageVariantSummaryParts,
    getEffectiveVariantIdForLink,
    getProductPageDefaultVariantSummaryParts,
    getSelectedProductDefaultVariantCaption,
} from './model'

function variantLabelChipClassNames(emphasized?: boolean) {
    return cn(
        'max-w-full min-w-0 whitespace-normal break-words rounded-md border border-border bg-muted/80 px-1.5 py-0.5 text-sm font-normal text-muted-foreground [overflow-wrap:anywhere]',
        emphasized &&
            'border-primary/25 bg-primary/5 font-medium text-foreground',
    )
}

/** Read-only chip matching {@link SelectedProductVariantButton} visuals (no button, no pencil). */
function ProductVariantLabelChip({
    label,
    emphasized,
}: {
    label: string
    emphasized?: boolean
}) {
    return (
        <span
            className={cn(
                'mx-0.5 inline-block align-baseline',
                variantLabelChipClassNames(emphasized),
            )}
            title={label}
        >
            {label}
        </span>
    )
}

function ProductPageDefaultSummaryInline({
    parts,
}: {
    parts: ProductPageVariantSummaryParts
}) {
    if ('text' in parts) {
        return <>{parts.text}</>
    }
    return (
        <>
            {parts.lead}
            <ProductVariantLabelChip label={parts.variantLabel} />
        </>
    )
}

interface StorefrontDefaultVariantPanelProps {
    link: AdminLandingPageLink
    onDismiss: () => void
    onConfirm: (nextLink: AdminLandingPageLink) => void
}

export const StorefrontDefaultVariantSelectionPanel: React.FC<
    StorefrontDefaultVariantPanelProps
> = ({ link, onDismiss, onConfirm }) => {
    const { variants, options } = useMemo(
        () =>
            primaryProductVariantsAndOptions({
                variants: link.variantInfo ?? [],
                options: link.optionInfo ?? [],
                virtualProductId: link.virtualProductId,
                productGroupKey: link.productGroupKey,
                shopifyId: link.shopifyId,
            }),
        [link],
    )

    const [isOverridden, setIsOverridden] = useState(
        () => !!link.defaultVariantOverrideId,
    )
    /** Product-level fields only (no storefront override); not edited in this panel. */
    const productLinkNoOverride = useMemo(
        (): AdminLandingPageLink => ({
            ...link,
            defaultVariantOverrideId: null,
        }),
        [link],
    )
    const [draftOverrideId, setDraftOverrideId] = useState<string>(() => {
        if (
            link.defaultVariantOverrideId &&
            variants.some((v) => v.shopifyId === link.defaultVariantOverrideId)
        ) {
            return link.defaultVariantOverrideId
        }
        return (
            getEffectiveVariantIdForLink(link) ?? variants[0]?.shopifyId ?? ''
        )
    })
    const onOverrideVariantSelect = useCallback((variantId: string) => {
        setDraftOverrideId(variantId)
    }, [])

    const handleIsOverriddenChange = (next: boolean) => {
        setIsOverridden(next)
        if (next) {
            const effective =
                getEffectiveVariantIdForLink(productLinkNoOverride) ??
                variants[0]?.shopifyId ??
                ''
            setDraftOverrideId(effective)
        }
    }

    const productPageVariantSummaryParts = useMemo(
        () => getProductPageDefaultVariantSummaryParts(productLinkNoOverride),
        [productLinkNoOverride],
    )

    const handleOK = (e: React.MouseEvent) => {
        e.stopPropagation()
        const resolvedOverrideId = isOverridden
            ? draftOverrideId || variants[0]?.shopifyId || null
            : null

        const nextLink: AdminLandingPageLink = {
            ...productLinkNoOverride,
            defaultVariantOverrideId: resolvedOverrideId,
        }
        onConfirm(nextLink)
        onDismiss()
    }

    return (
        <div
            className="bg-muted/30 mt-2 rounded-md border p-3 border-border"
            onPointerDown={(e) => e.stopPropagation()}
        >
            <Medium className="mb-1.5 block font-semibold text-foreground">
                Select Default Variant for this Storefront
            </Medium>

            <div className="mb-4">
                <Muted className="mb-3 block leading-snug">
                    Choose whether this storefront follows the product
                    page&apos;s default variant or sets its own.
                </Muted>
                <RadioGroup
                    value={isOverridden ? 'true' : 'false'}
                    onValueChange={(v) =>
                        handleIsOverriddenChange(v === 'true')
                    }
                    className="grid gap-3"
                >
                    <div className="flex items-start gap-2.5">
                        <RadioGroupItem
                            value="false"
                            id={`storefront-variant-inherit-${link.linkHash}`}
                            className="mt-0.5"
                        />
                        <Label
                            htmlFor={`storefront-variant-inherit-${link.linkHash}`}
                            className="cursor-pointer text-sm font-normal leading-snug"
                        >
                            <span className="block font-medium text-foreground">
                                Use the Product Level Default
                            </span>
                            <span className="mt-1 block text-sm leading-snug text-muted-foreground">
                                <ProductPageDefaultSummaryInline
                                    parts={productPageVariantSummaryParts}
                                />
                            </span>
                        </Label>
                    </div>
                    <div className="flex items-start gap-2.5">
                        <RadioGroupItem
                            value="true"
                            id={`storefront-variant-override-${link.linkHash}`}
                            className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                            <Label
                                htmlFor={`storefront-variant-override-${link.linkHash}`}
                                className="cursor-pointer text-sm font-normal leading-snug"
                            >
                                <span className="font-medium text-foreground">
                                    Choose a default variant for this storefront
                                    only
                                </span>
                            </Label>
                            {isOverridden && (
                                <div className="mt-3 flex flex-col gap-4 border-l-2 pl-3 border-border">
                                    <ManualDefaultVariantSelector
                                        variants={variants}
                                        options={options}
                                        defaultVariantId={
                                            draftOverrideId ||
                                            variants[0]?.shopifyId ||
                                            null
                                        }
                                        onVariantSelect={
                                            onOverrideVariantSelect
                                        }
                                        showVariantImagesAsColors={
                                            link.showVariantImagesAsColors ??
                                            false
                                        }
                                        showVariantImagesAsColorsToggle={false}
                                        reactKeysPrefix="storefront"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </RadioGroup>
            </div>

            <div className="mt-1 flex flex-wrap justify-end gap-2 border-t pt-3 border-border">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation()
                        onDismiss()
                    }}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={handleOK}
                >
                    OK
                </Button>
            </div>
        </div>
    )
}

/**
 * Inline value treatment instead of wrapping ASCII quotes, so labels that contain
 * quotation marks (e.g. 12" measurements) stay unambiguous.
 */
function SelectedProductVariantButton({
    label,
    emphasized,
    onEditClick,
}: {
    label: string
    emphasized?: boolean
    onEditClick?: () => void
}) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
                'mx-0.5 h-auto min-h-0 max-w-full justify-start gap-1 px-1.5 py-0.5 align-baseline shadow-none hover:opacity-100',
                variantLabelChipClassNames(emphasized),
                emphasized ? 'hover:bg-primary/10' : 'hover:bg-muted',
            )}
            title={label}
            onClick={(e) => {
                e.stopPropagation()
                onEditClick?.()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
        >
            <span className="min-w-0 flex-1 text-left">{label}</span>
            <Icon type="pencil" color="currentColor" width={14} height={14} />
        </Button>
    )
}

export function SelectedProductDefaultVariantLine({
    link,
    onApplyLinkUpdate,
}: {
    link: AdminLandingPageLink
    onApplyLinkUpdate: (
        linkHash: string,
        nextLink: AdminLandingPageLink,
    ) => void
}) {
    const caption = useMemo(
        () => getSelectedProductDefaultVariantCaption(link),
        [link],
    )
    const [isEditing, setIsEditing] = useState(false)

    if (!caption) {
        return null
    }

    if (isEditing) {
        return (
            <StorefrontDefaultVariantSelectionPanel
                link={link}
                onDismiss={() => setIsEditing(false)}
                onConfirm={(next) => onApplyLinkUpdate(link.linkHash, next)}
            />
        )
    }

    return (
        <div className="mt-1.5 w-full min-w-0">
            <XSmall className="block w-full min-w-0 leading-relaxed text-muted-foreground">
                {caption.prefix}
                <SelectedProductVariantButton
                    label={caption.label}
                    emphasized={caption.emphasized}
                    onEditClick={() => setIsEditing(true)}
                />
            </XSmall>
        </div>
    )
}
